import React from "react";
import {
  Box,
  Container,
  Divider,
  Grid,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { StyledIconButton } from "../../../components/StyledComponents/Buttons/AuradineButtons.js";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import {
  BodyLarge,
  H6HeadlineSmall,
  H8TitleMedium,
  DataLegend,
  LabelLarge,
  BodySmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import AuradineErrorState from "../../../components/StyledComponents/EmptyStates/AuradineErrorState.tsx";
import { useHistory } from "react-router-dom";
import { FetchHeatMapDataBySerialNumber } from "../../../api/api.js";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import { useQuery } from "@tanstack/react-query";
import { computeRowsUpdates } from "@mui/x-data-grid/hooks/features/rows/gridRowsUtils";
import { objectShallowCompare } from "@mui/x-data-grid/hooks/utils/useGridSelector";
import AuradineDataNotFoundState from "../../../components/StyledComponents/EmptyStates/AuradineDataNotFoundState.tsx";
import {
  AT1_chipLayout,
  AI25_chipLayout,
  AT28_chipLayout,
  AI36_chipLayout,
  AD_chipLayout,
  AH_chipLayout,
  AI3680S_chipLayout,
} from "../../../constants.js";
import {
  flatMap,
  get,
  has,
  hasIn,
  isEmpty,
  sortBy,
} from "lodash";
import StyledToolTip from "../../../components/StyledComponents/ToolTip/ToolTip.js";
import { useAuth0 } from "@auth0/auth0-react";
import PageWrapper from "../../../components/PageWrapper/PageWrapper.js";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";

const ColorSquare = (props) => {
  const { color } = props;
  return (
    <div
      style={{
        width: "12px",
        height: "12px",
        backgroundColor: `${color}`,
      }}
    />
  );
};

const createHashboardChipData = () => {
  const hashboardChipMockData = [];
  for (let i = 1; i <= 200; i++) {
    hashboardChipMockData.push({
      id: i + 100,
      frequency: Math.floor(Math.random() * 100),
      temperature: Math.floor(Math.random() * 100),
    });
  }

  return hashboardChipMockData;
};

const ToolTipBody = React.memo((props) => {
  const { data } = props;

  console.log("tooltip Data", data);
  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      padding={"0.5rem"}
      gap={1}
      className={"tooltip"}
    >
      <Box
        display={"flex"}
        flexDirection={"row"}
        justifyContent={"flex-start"}
        className={"tooltip-header"}
      >
        <H8TitleMedium>{`ID: ${data.id}`}</H8TitleMedium>
      </Box>
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={4}
      >
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={4}
          className={"frequency-datapoint"}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: data.freqColor }}
          >
            vital_signs
          </span>
          <BodySmall>{`${data.frequency?.toFixed(
            0
          )} MHz`}</BodySmall>
        </Box>
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={4}
          className={"voltage-datapoint"}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: data.voltageColor }}
          >
            electric_bolt
          </span>
          <BodySmall>{`${(data.voltage
            ? data.voltage * 1000
            : 0
          ).toFixed(0)} mV`}</BodySmall>
        </Box>
        <Box
          display={"flex"}
          flexDirection={"row"}
          gap={4}
          className={"temperature-datapoint"}
        >
          <span
            className="material-symbols-outlined"
            style={{ color: data.tempColor }}
          >
            thermometer
          </span>
          <BodySmall>{`${data.temperature?.toFixed(
            0
          )} °C`}</BodySmall>
        </Box>
      </Box>
    </Box>
  );
});

const ChipComponent = (props) => {
  const { data, index } = props;
  const { colors } = useTheme();
  const emphasisOnChipRequired =
    data?.tempColor === "#BA1A1A" ||
    data?.tempColor === "#FB532D" ||
    data?.tempColor === "#FFB94D" ||
    data?.tempColor === "#77767A" ||
    data?.tempColor === "#FFFFFF "
      ? true
      : false;
  return (
    <StyledToolTip
      colors={colors}
      title={<ToolTipBody data={data} />}
      placement={"bottom"}
      arrow
      disableHoverListener={data?.id === -1}
      disableFocusListener={data?.id === -1}
      disableTouchListener={data?.id === -1}
    >
      {data?.id !== -1 ? (
        <Box
          key={index}
          display="flex"
          flexDirection="row"
          padding={"0.375rem 0.5rem"}
          gap={1}
          borderRadius={"0.5rem"}
          border={
            [data.tempColor].includes("#BA1A1A")
              ? `1px solid ${colors.heatmapBorder.critical}`
              : [data.tempColor].includes("#FB532D")
              ? `1px solid ${colors.heatmapBorder.danger}`
              : [data.tempColor].includes("#FFB94D")
              ? `1px solid ${colors.heatmapBorder.caution}`
              : [data.tempColor].includes("#77767A")
              ? `1px solid ${colors.heatmapBorder.neutral}`
              : [data.tempColor].includes("#FFFFFF")
              ? `1px solid #FFFFFF`
              : `1px solid ${colors.heatmapBorder.default}`
          }
          flexShrink={0}
          flexGrow={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
          sx={{ cursor: "pointer !important" }}
        >
          <LabelLarge
            sx={{
              fontColor: "#1A1B22",
              opacity: emphasisOnChipRequired
                ? "1"
                : "0.38",
            }}
          >
            {data.id}
          </LabelLarge>

          <span
            className="material-symbols-outlined"
            style={{
              width: "1.125rem",
              height: "1.125rem",
              color: data.freqColor ?? "#000",
              opacity: emphasisOnChipRequired
                ? "1"
                : "0.38",
            }}
          >
            vital_signs
          </span>
          <span
            className="material-symbols-outlined"
            style={{
              width: "1.125rem",
              height: "1.125rem",
              color: data.voltageColor ?? "#000",
              opacity: emphasisOnChipRequired
                ? "1"
                : "0.38",
            }}
          >
            electric_bolt
          </span>
          <span
            className="material-symbols-outlined"
            style={{
              width: "1.125rem",
              height: "1.125rem",
              color: data.tempColor ?? "#000",
              opacity: emphasisOnChipRequired
                ? "1"
                : "0.38",
            }}
          >
            thermometer
          </span>
        </Box>
      ) : (
        <Box
          key={index}
          display="flex"
          flexDirection="row"
          padding={"0.375rem 0.5rem"}
          gap={1}
          borderRadius={"0.5rem"}
          border={
            [data.tempColor].includes("#BA1A1A")
              ? `1px solid ${colors.heatmapBorder.critical}`
              : [data.tempColor].includes("#FB532D")
              ? `1px solid ${colors.heatmapBorder.danger}`
              : [data.tempColor].includes("#FFB94D")
              ? `1px solid ${colors.heatmapBorder.caution}`
              : [data.tempColor].includes("#77767A")
              ? `1px solid ${colors.heatmapBorder.neutral}`
              : [data.tempColor].includes("#FFFFFF")
              ? "1px solid #FFFFFF"
              : "none"
          }
          flexShrink={0}
          flexGrow={1}
          alignItems={"center"}
          justifyContent={"space-between"}
          flexWrap={"wrap"}
          sx={{ cursor: "pointer !important" }}
        >
          <Box></Box>
          <Box></Box>
          <Box></Box>
          <Box></Box>
        </Box>
      )}
    </StyledToolTip>
  );
};

const HashboardPageHeader = () => {
  const navigate = useHistory();  
  const { colors } = useTheme();
  return (
    <Box
      display="flex"
      flexDirection="row"
      gap={2.48}
      marginBottom={"1.5rem"}
    >
      <Box
        display="flex"
        flexDirection="column"
        minWidth={"2.98rem"}
        justifyContent="flex-start"
        alignItems={"center"}
      >
        <StyledIconButton colors={colors} onClick={() => navigate.goBack()}>
          {" "}
          <ArrowBackIosNewIcon
            fontSize="medium"
          />{" "}
        </StyledIconButton>
      </Box>

      <Box
        display="flex"
        flexDirection="column"
        gap={4}
        justifyContent="flex-start"
      >
        <H6HeadlineSmall>Hash Boards</H6HeadlineSmall>
        <BodyLarge>
          Monitor key environmental metrics—frequency,
          temperature, and voltage—across every ASIC to
          quickly assess their health and identify areas
          needing attention. Stay on top of ASIC performance
          to ensure optimal mining efficiency.
        </BodyLarge>
      </Box>
    </Box>
  );
};

const HashboardHeatMapTableLegend = () => {
  const { colors } = useTheme();
  return (
    <Grid container display="flex" gap={4} flexGrow={1}>
      <Grid item xs={6}>
        <Box
          width={"100%"}
          flexGrow={1}
          className={"legend-top-left-container"}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-end"}
          gap={4}
        >
          <Box
            display="flex"
            className={"legend-title-with-icon"}
            flexDirection="row"
            alignItems={"center"}
            gap={4}
          >
            <H8TitleMedium>Frequency</H8TitleMedium>
            <span
              className="material-symbols-outlined"
              style={{ height: "24px", width: "24px", color: colors?.text?.muted }}
            >
              vital_signs
            </span>
            <Box
              display={"flex"}
              gap={8}
              alignItems={"center"}
            >
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#77767A" />
                <DataLegend>Chip Error</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#2D55C9" />
                <DataLegend>0 MHz - 199.99 MHz</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#678AFF" />
                <DataLegend>200 MHz - 2000 MHz</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#CC2932" />
                <DataLegend>2001 MHz - 3000 MHz</DataLegend>
              </Box>
              {/* <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#00277E" />
                <DataLegend>2001 - 3000</DataLegend>
              </Box> */}
              <Divider
                orientation="vertical"
                sx={{
                  width: "0.0625rem",
                  height: "1.5rem",
                }}
                flexItem
              />
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={5.5}>
        <Box
          width={"100%"}
          flexGrow={1}
          className={"legend-top-left-container"}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"flex-start"}
          gap={4}
        >
          <Box
            display="flex"
            className={"legend-title-with-icon"}
            flexDirection="row"
            alignItems={"center"}
            gap={4}
          >
            <H8TitleMedium>Voltage</H8TitleMedium>
            <span
              className="material-symbols-outlined"
              style={{ height: "24px", width: "24px", color: colors?.text?.muted }}
            >
              electric_bolt
            </span>
            <Box
              display={"flex"}
              gap={8}
              alignItems={"center"}
            >
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#77767A" />
                <DataLegend>Chip Error</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#2D55C9" />
                <DataLegend>0 mV - 179.99 mV</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#008A37" />
                <DataLegend>180 mV - 449.99 mV</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#CC2932" />
                <DataLegend>450 mV - 1000 mV</DataLegend>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Box
          width={"100%"}
          flexGrow={1}
          className={"legend-top-left-container"}
          display={"flex"}
          flexDirection={"row"}
          justifyContent={"center"}
          gap={4}
        >
          <Box
            display="flex"
            className={"legend-title-with-icon"}
            flexDirection="row"
            alignItems={"center"}
            gap={4}
          >
            <H8TitleMedium>Temperature</H8TitleMedium>
            <span
              className="material-symbols-outlined"
              style={{ height: "24px", width: "24px", color: colors?.text?.muted }}
            >
              thermometer
            </span>
            <Box
              display={"flex"}
              gap={8}
              alignItems={"center"}
            >
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#FFFFFF" />
                <DataLegend>No chip</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#77767A" />
                <DataLegend>Chip Error</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#2D55C9" />
                <DataLegend>-50°C - 25°C</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#4B6FE3" />
                <DataLegend>25°C - 49°C</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#008A37" />
                <DataLegend>50°C - 74°C</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#FFB94D" />
                <DataLegend>75°C - 89°C</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#FB532D" />
                <DataLegend>90°C - 105°C</DataLegend>
              </Box>
              <Box
                className="legend-values"
                display={"flex"}
                flexDirection="row"
                gap={2}
              >
                <ColorSquare color="#CC2932" />
                <DataLegend>106°C - 120°C</DataLegend>
              </Box>
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

const heatmapColors = (value, type) => {
  if (value === undefined || value === null)
    return undefined; // check for undefined or null

  if (type === "frequency") {
    if (value >= 0 && value <= 199.99) return "#2D55C9";
    if (value >= 200 && value <= 2000.99) return "#678AFF";
    if (value >= 2001 && value <= 3000) return "#CC2932";
  }

  if (type === "temperature") {
    if (value >= -2000 && value <= -1000.01)
      return "#FFFFFF";

    if (value >= -1000 && value <= -50.01) return "grey";
    if (value >= -50 && value <= 24.99) return "#2D55C9";
    if (value >= 25 && value <= 49.99) return "#8FA7FF";
    if (value >= 50 && value <= 74.99) return "#008A37";
    if (value >= 75 && value <= 89.99) return "#FFB94D";
    if (value >= 90 && value <= 105.99) return "#FB532D";
    if (value >= 106 && value <= 120) return "#CC2932";
  }

  if (type === "voltage") {
    let voltValue = value * 1000;
    if (voltValue >= 0 && voltValue <= 179.99)
      return "#2D55C9";
    if (voltValue >= 180 && voltValue <= 449.99)
      return "#008A37";
    if (voltValue >= 450 && voltValue <= 1000)
      return "#CC2932";
  }

  return undefined; // default if no conditions are met
};
const findChipLayout = (model) => {
  if (!model) return "";
  if (
    model.toLowerCase().includes("at1") ||
    model.toLowerCase().includes("at 1")
  ) {
    return AT1_chipLayout;
  } else if (model.toLowerCase().includes("ai25")) {
    return AI25_chipLayout;
  } else if (model.toLowerCase().includes("at28")) {
    return AT28_chipLayout;
  } else if (model.toLowerCase().includes("ad")) {
    return AD_chipLayout;
  } else if (model.toLowerCase().includes("ai36")) {
    return AI36_chipLayout;
  } else if (model.toLowerCase().includes("ah")) {
    return AH_chipLayout;
  } else if (model.toLowerCase().includes("ai3680s")) {
    return AI3680S_chipLayout;
  }

  return ""; // default if no conditions are met
};

const HashboardHeatMap = () => {
  const { colors } = useTheme();
  const [selectedTab, setSelectedTab] = React.useState(0);
  const { authToken } = useAppContextProvider();
  const mockData = createHashboardChipData();
  const navigate = useHistory();
  const urlParams = new URLSearchParams(
    window.location.search
  );
  const { getAccessTokenSilently } = useAuth0();
  const serialNo = urlParams.get("serialNo");
  const trimmedSerialNo = serialNo ? serialNo?.trim() : "";

  const isValidSerialNo =
    !!trimmedSerialNo &&
    trimmedSerialNo !== "undefined" &&
    trimmedSerialNo !== "null" &&
    trimmedSerialNo !== "";
  // console.log("serialNo", serialNo, isValidSerialNo);

  const {
    data: HeatMapData,
    isLoading,
    isError,
    isFetched,
  } = useQuery({
    queryKey: ["hashboardHeatMap", serialNo],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return FetchHeatMapDataBySerialNumber({
        authToken: token,
        serialNumber: serialNo,
      });
    },
    enabled: isValidSerialNo,
    staleTime: 1000 * 60 * 5,
  });
  let importantInfoMissing = false;
  const hashboardHeatMapData = HeatMapData?.docs;
  if (
    isFetched &&
    !isEmpty(hashboardHeatMapData) &&
    !hasIn(hashboardHeatMapData, "Frequency") &&
    !hasIn(hashboardHeatMapData, "Temperature") &&
    !hasIn(hashboardHeatMapData, "Voltage")
  ) {
    importantInfoMissing = true;
  }

  const hashboardHeatMapDataMapped =
    HeatMapData?.docs?.map((data) => {
      const { Model, Frequency, Temperatures, voltage } =
        data;
      console.log("model", Model);

      const FrequencyData = Frequency?.Frequency;
      const FrequencyDataMap = FrequencyData?.filter(
        (data) => {
          const { Name: name } = data;
          return name.toLowerCase().includes("hash board");
        }
      ).reduce((acc, data) => {
        return {
          ...acc,
          [data?.Name]: data?.ChipFrequency,
        };
      }, {});
      console.log("FrequencyDataMap", FrequencyDataMap);

      const TemperatureData = Temperatures?.Temperature;
      console.log("TemperatureData", TemperatureData);
      const TemperatureDataMap = TemperatureData?.filter(
        (data) =>
          data?.Name?.toLowerCase().includes("hash board")
      ).reduce((acc, data) => {
        return { ...acc, [data?.Name]: data?.ChipTemp };
      }, {});
      console.log("TemperatureDataMap", TemperatureDataMap);

      const voltageData = voltage?.Voltage;
      const voltageDataMap = voltageData
        ?.filter((data) =>
          data?.Name?.toLowerCase().includes("hash board")
        )
        .reduce((acc, data) => {
          return {
            ...acc,
            [data?.Name]: data?.ChipVoltage,
          };
        }, {});
      console.log("voltageDataMap", voltageDataMap);
      console.log("layout", findChipLayout(Model));
      const chipLayout = flatMap(findChipLayout(Model));

      const hashboardsInformation = [
        ...new Set([
          ...Object.keys(FrequencyDataMap || {}),
          ...Object.keys(TemperatureDataMap || {}),
          ...Object.keys(voltageDataMap || {}),
        ]),
      ];

      const TempFreqAndVoltData = hashboardsInformation.map(
        (boardName) => {
          const freqArr = FrequencyDataMap
            ? FrequencyDataMap[boardName]
            : [];
          const tempArr = TemperatureDataMap
            ? TemperatureDataMap[boardName]
            : [];
          const voltArr = voltageDataMap
            ? voltageDataMap[boardName]
            : [];

          // Get all unique chip IDs for this board
          const chipIds = [
            ...new Set([
              ...(freqArr
                ? freqArr.map((chip) => chip.ID)
                : []),
              ...(tempArr
                ? tempArr.map((chip) => chip.ID)
                : []),
              ...(voltArr
                ? voltArr.map((chip) => chip.ID)
                : []),
            ]),
          ];

          // Map over all chip IDs
          const chips = sortBy(
            chipIds.map((chipId) => {
              const freqObj = freqArr
                ? freqArr.find(
                    (frequency) => frequency.ID === chipId
                  )
                : {};
              const tempObj = tempArr
                ? tempArr.find(
                    (temperature) =>
                      temperature.ID === chipId
                  )
                : {};
              const voltObj = voltArr
                ? voltArr.find(
                    (voltage) => voltage.ID === chipId
                  )
                : {};

              return {
                id: chipId,
                frequency: freqObj?.Frequency
                  ? freqObj?.Frequency
                  : 0,
                temperature: tempObj?.Temperature
                  ? tempObj?.Temperature
                  : 0,
                voltage: voltObj?.Voltage
                  ? voltObj?.Voltage
                  : 0,
                voltageColor: heatmapColors(
                  voltObj?.Voltage ? voltObj?.Voltage : 0,
                  "voltage"
                ),
                tempColor: heatmapColors(
                  tempObj?.Temperature
                    ? tempObj?.Temperature
                    : 0,
                  "temperature"
                ),
                freqColor: heatmapColors(
                  freqObj?.Frequency
                    ? freqObj?.Frequency
                    : 0,
                  "frequency"
                ),
              };
            }),
            (arrayItem) => {
              return chipLayout.findIndex(
                (chipId) => arrayItem.id === chipId
              );
            }
          );

          const sortedData = chipLayout.map(
            (chipId, index) => {
              if (Number(chipId) === -1) {
                return {
                  id: -1,
                  frequency: undefined,
                  temperature: undefined,
                  voltage: undefined,
                  voltageColor: undefined,
                  tempColor: undefined,
                  freqColor: undefined,
                };
              }
              return (
                chips.find(
                  (data) => data.id === chipId
                ) || {
                  id: chipId,
                  frequency: 0,
                  temperature: 0,
                  voltage: 0,
                  voltageColor: heatmapColors(0, "voltage"),
                  tempColor: heatmapColors(
                    0,
                    "temperature"
                  ),
                  freqColor: heatmapColors(0, "frequency"),
                }
              );
            }
          );

          return { [boardName]: sortedData };
        }
      );
      return { Model, TempFreqAndVoltData };
    }) ?? [];

  const hashboardHeatMapDataFinal =
    hashboardHeatMapDataMapped?.reduce((acc, data) => {
      return { ...acc, ...data };
    }, {}) ?? [];
  console.log(
    "hashboardHeatMapDataFinal",
    hashboardHeatMapDataFinal
  );
  const numberOfHashboards = !isEmpty(
    hashboardHeatMapDataFinal
  )
    ? hashboardHeatMapDataFinal?.TempFreqAndVoltData.length
    : 3;

  console.log(hashboardHeatMapDataFinal);

  return (
    <PageWrapper>
      <HashboardPageHeader />

      <Box
        className={"miner-page-table"}
        display={"flex"}
        flexDirection={"column"}
        // backgroundColor={"#F3F2FD"}
        sx={{
          backgroundColor: colors?.background?.paper, 
        }}
        padding={"0.75rem 0.75rem 0rem 0.75rem"}
        borderRadius={"1rem"}
        component={"div"}
      >
        <HashboardHeatMapTableLegend />
        {!isValidSerialNo && (
          <Box
            display={"flex"}
            flexDirection={"row"}
            pb={"2%"}
            flexGrow={1}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <AuradineErrorState
              message={"Please provide a serial number!!"}
            />
          </Box>
        )}
        <Tabs
          value={selectedTab}
          onChange={(event, newValue) =>
            setSelectedTab(newValue)
          }
          variant="fullWidth"
          textColor="inherit"
          indicatorColor="primary"
          sx={{
            paddingBottom: "0.5rem",
            "& .MuiTab-root": {
              textTransform: "none",
              color: colors?.text?.label,  
              fontFamily: "Roboto",
              fontSize: "1rem",
              fontWeight: "500",
              lineHeight: "1.25rem",
            },
            "& .Mui-selected": {
              color: colors?.primary?.main, // Custom text color for selected tab
            },
            "& .MuiTabs-indicator": {
              display: "flex",
              justifyContent: "center",
              backgroundColor: colors?.primary?.main,
              maxWidth: "5%",
              height: "4px",
              borderTopLeftRadius: 100,
              borderTopRightRadius: 100,
              marginLeft:
                numberOfHashboards === 2
                  ? "22.5%"
                  : numberOfHashboards === 1
                  ? "47.5%"
                  : "14%",
            },
            "& .MuiTabs-indicatorSpan": {
              maxWidth: " 10 !important",
              width: "100%",
              backgroundColor: colors?.primary?.main,
            },
          }}
        >
          {isLoading && (
            <Skeleton
              animation="wave"
              variant="rectangular"
              width={"100%"}
              height={"3.0625rem"}
              sx={{
                backgroundColor: colors?.background?.muted,
                borderRadius: "0.5rem",
              }}
            />
          )}
          {isFetched &&
            !isEmpty(hashboardHeatMapDataFinal) &&
            Array.isArray(
              hashboardHeatMapDataFinal?.TempFreqAndVoltData
            ) &&
            hashboardHeatMapDataFinal?.TempFreqAndVoltData
              .length > 0 &&
            hashboardHeatMapDataFinal?.TempFreqAndVoltData.map(
              (data, index) => (
                <Tab label={`Board ${index + 1}`} />
              )
            )}
        </Tabs>

        {isLoading && (
          <Box
            width={"100%"}
            height={"57vh"}
            overflow={"auto"}
          >
            <Grid container spacing={2} margin={0}>
              {mockData.map((data, index) => (
                <Grid item xs={1} key={index}>
                  <Skeleton
                    animation="wave"
                    variant="rectangular"
                    width={"100%"}
                    height={"2rem"}
                    sx={{
                      backgroundColor:
                        colors?.background?.muted,
                      borderRadius: "0.5rem",
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
        {isError && !isFetched && (
          <Box
            display={"flex"}
            flexDirection={"row"}
            flexGrow={1}
            pb={"2%"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            {" "}
            <AuradineErrorState message="Something went wrong!!" />{" "}
          </Box>
        )}
        {isFetched &&
          HeatMapData?.status.toLowerCase() === "error" && (
            <Box
              display={"flex"}
              flexDirection={"row"}
              flexGrow={1}
              pb={"2%"}
              justifyContent={"center"}
              alignItems={"center"}
            >
              {" "}
              <AuradineErrorState
                message={`${HeatMapData?.msg}`}
              />{" "}
            </Box>
          )}
        {isFetched &&
          HeatMapData?.status.toLowerCase() === "ok" &&
          ((hashboardHeatMapDataFinal &&
            hasIn(
              hashboardHeatMapDataFinal,
              "TempFreqAndVoltData"
            ) &&
            Array.isArray(
              hashboardHeatMapDataFinal?.TempFreqAndVoltData
            ) &&
            hashboardHeatMapDataFinal?.TempFreqAndVoltData
              .length === 0) ||
            isEmpty(hashboardHeatMapDataFinal)) && (
            <Box
              display={"flex"}
              flexDirection={"row"}
              pb={"2%"}
              flexGrow={1}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <AuradineDataNotFoundState
                message={`No data for device with serial number ${serialNo}`}
              />
            </Box>
          )}
        {isFetched &&
          HeatMapData?.status.toLowerCase() === "ok" &&
          !isEmpty(hashboardHeatMapDataFinal) &&
          hasIn(
            hashboardHeatMapDataFinal,
            "TempFreqAndVoltData"
          ) &&
          Array.isArray(
            hashboardHeatMapDataFinal?.TempFreqAndVoltData
          ) &&
          hashboardHeatMapDataFinal?.TempFreqAndVoltData
            .length > 0 &&
          hashboardHeatMapDataFinal?.TempFreqAndVoltData.map(
            (hashboardData, index) => {
              return (
                selectedTab === index && (
                  <Box
                    width={"100.0%"}
                    height={"calc(100vh - 23rem)"}
                    sx={{
                      overflowY: "auto",
                      overflowX: "hidden",
                    }}
                  >
                    <Grid container spacing={1} margin={0}>
                      {hashboardData &&
                        hashboardData[
                          `Hash Board ${index + 1}`
                        ] === undefined && (
                          <Box
                            display={"flex"}
                            flexDirection={"row"}
                            flexGrow={1}
                            pb={"2%"}
                            justifyContent={"center"}
                            alignItems={"center"}
                          >
                            <AuradineDataNotFoundState
                              message={`No data for Hash Board ${
                                index + 1
                              }`}
                            />
                          </Box>
                        )}
                      {hashboardData &&
                        hashboardData[
                          `Hash Board ${index + 1}`
                        ] &&
                        hashboardData[
                          `Hash Board ${index + 1}`
                        ].map((data, index) => (
                          <Grid item xs={0.99} key={index}>
                            <ChipComponent
                              data={data}
                              index={index}
                            />
                          </Grid>
                        ))}
                    </Grid>
                  </Box>
                )
              );
            }
          )}
      </Box>
    </PageWrapper>
  );
};

export default HashboardHeatMap;
